import { useState, useEffect } from 'react';
import type { SchoolRequestDto } from '../api/school-requests.api';
import type { SchoolDataImportDto } from '../../schools/api/schools.api';
import { schoolsApi } from '../../schools/api/schools.api';

// @ts-ignore - JSX component
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { CodSeLaAprobare } from './CodSeLaAprobare';

interface EditableFormDialogProps {
    isOpen: boolean;
    onClose: () => void;
    formData: SchoolRequestDto | null;
    /** Codul SE al randului din grila (pe cererile in asteptare: al scolii gasite dupa SIIIR). */
    codSeRand?: string | null;
    /** Profesorul cererii, din randul grilei. Doar afisat: contul lui se editeaza din Raport Utilizatori. */
    profesor?: { nume?: string | null; telefon?: string | null; email?: string | null } | null;
    counties: string[];
    /**
     * Erorile serverului de la ultima salvare respinsa, pe cheile din `handleFieldChange`
     * (`telefon`, `adresa.oras`, ...). Se afiseaza sub camp, la fel ca validarea locala.
     */
    serverErrors?: Record<string, string>;
    onSave: (data: SchoolRequestDto) => void;
    loading: boolean;
}

export function EditableFormDialog({
    isOpen,
    onClose,
    formData,
    codSeRand,
    profesor,
    counties,
    serverErrors,
    onSave,
    loading
}: EditableFormDialogProps) {
    const [editedData, setEditedData] = useState<SchoolRequestDto | null>(null);
    const [siiirSearch, setSiiirSearch] = useState('');
    const [siiirResults, setSiiirResults] = useState<SchoolDataImportDto[]>([]);
    const [loadingSiiir, setLoadingSiiir] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen && formData) {
            setEditedData({ ...formData });
        } else if (!isOpen) {
            setEditedData(null);
            setSiiirSearch('');
            setSiiirResults([]);
            setErrors({});
        }
    }, [isOpen, formData]);

    // Salvare respinsa de server: erorile lui ajung in aceeasi stare ca validarea locala, deci se
    // sterg la fel, camp cu camp, cand adminul corecteaza.
    useEffect(() => {
        if (serverErrors && Object.keys(serverErrors).length > 0) {
            setErrors(serverErrors);
        }
    }, [serverErrors]);

    // Search SIIIR codes with pagination
    const searchSiiir = async (search: string) => {
        if (!search || search.length < 2) {
            setSiiirResults([]);
            return;
        }

        setLoadingSiiir(true);
        try {
            const result = await schoolsApi.getImportData(
                { codSiiirUnitate: search },
                { page: 0, size: 20 } // Paginated search - only 20 results
            );
            setSiiirResults(result.content || []);
        } catch (error: unknown) {
            console.error('Error searching SIIIR:', error);
            setSiiirResults([]);
        } finally {
            setLoadingSiiir(false);
        }
    };

    // Handle SIIIR selection and auto-populate fields
    const handleSiiirSelect = (school: SchoolDataImportDto) => {
        if (!editedData) return;

        const updated = { ...editedData };

        // Set SIIIR code
        if (school.codSiiirUnitate) updated.codSiiirUnitate = school.codSiiirUnitate;

        // Auto-populate school fields
        if (school.numeInstitutieInvatamant) updated.numeInstitutieInvatamant = school.numeInstitutieInvatamant;
        if (school.cui) updated.cui = school.cui;
        if (school.telefon) updated.telefon = school.telefon;

        // Auto-populate address fields with required fields
        if (!updated.adresa && school.judet && school.oras) {
            updated.adresa = {
                judet: school.judet,
                oras: school.oras
            };
        } else if (updated.adresa) {
            if (school.judet) updated.adresa.judet = school.judet;
            if (school.oras) updated.adresa.oras = school.oras;
        }

        // Add optional address fields only if adresa exists
        if (updated.adresa) {
            if (school.comuna) updated.adresa.comuna = school.comuna;
            if (school.sat) updated.adresa.sat = school.sat;
            if (school.strada) updated.adresa.strada = school.strada;
            if (school.numar) updated.adresa.numar = school.numar;
            if (school.codPostal) updated.adresa.codPostal = school.codPostal;
        }

        // Codul SE nu se mai cauta aici (owner, 29.09): il da serverul, doar la aprobare —
        // al scolii existente dupa SIIIR, sau urmatorul liber. Backendul il ignora oricum.

        setEditedData(updated);
        setSiiirResults([]);
        setSiiirSearch(school.codSiiirUnitate || '');
    };

    const handleFieldChange = (path: string, value: any) => {
        if (!editedData) return;

        const updated = { ...editedData };
        const parts = path.split('.');

        if (parts.length === 1) {
            (updated as any)[parts[0]] = value;
        } else if (parts.length === 2) {
            // Initialize address with required fields if it doesn't exist
            if (!updated.adresa) updated.adresa = { judet: '', oras: '' };
            (updated.adresa as any)[parts[1]] = value;
        }

        setEditedData(updated);

        // Clear error for this field
        if (errors[path]) {
            const newErrors = { ...errors };
            delete newErrors[path];
            setErrors(newErrors);
        }
    };

    const handleSave = () => {
        if (!editedData) return;

        // Basic validation
        const newErrors: Record<string, string> = {};
        if (!editedData.numeInstitutieInvatamant) newErrors.numeInstitutieInvatamant = 'Numele instituției este obligatoriu';
        if (!editedData.codSiiirUnitate) newErrors.codSiiirUnitate = 'Codul SIIIR este obligatoriu';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSave(editedData);
    };

    if (!editedData) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[800px] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Corectare Formular de Înscriere</DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* SIIIR Code Selection */}
                    <div>
                        <h3 className="ecotic-dialog-section-title">
                            Selecție Școală (Cod SIIIR)
                        </h3>
                        <div className="relative">
                            <label className="text-small font-medium text-gray-700">
                                Căutare Cod SIIIR: <span className="text-error">*</span>
                            </label>
                            <input
                                type="text"
                                value={siiirSearch}
                                onChange={(e) => {
                                    setSiiirSearch(e.target.value);
                                    searchSiiir(e.target.value);
                                }}
                                placeholder="Introduceți cod SIIIR pentru căutare..."
                                className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                disabled={loading}
                            />
                            {loadingSiiir && (
                                <div className="absolute right-3 top-9 text-xs text-gray-500">Se caută...</div>
                            )}
                            {siiirResults.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {siiirResults.map((school, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => handleSiiirSelect(school)}
                                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-small border-b border-gray-200 last:border-b-0"
                                        >
                                            <div className="font-medium">{school.codSiiirUnitate}</div>
                                            <div className="text-xs text-gray-600">{school.numeInstitutieInvatamant}</div>
                                            <div className="text-xs text-gray-500">{school.judet}, {school.oras}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {errors.codSiiirUnitate && <p className="mt-1 text-xs text-error">{errors.codSiiirUnitate}</p>}
                        </div>
                    </div>

                    {/* Profesorul cererii: adminul suna sau scrie de aici, fara sa caute randul in grila. */}
                    {profesor && (
                        <div data-profesor-cerere>
                            <h3 className="ecotic-dialog-section-title">Profesor coordonator</h3>
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-small sm:grid-cols-3">
                                <div>
                                    <dt className="font-medium text-gray-700">Nume:</dt>
                                    <dd className="break-words">{profesor.nume || '-'}</dd>
                                </div>
                                <div>
                                    <dt className="font-medium text-gray-700">Telefon:</dt>
                                    <dd>{profesor.telefon ? <a className="underline" href={`tel:${profesor.telefon}`}>{profesor.telefon}</a> : '-'}</dd>
                                </div>
                                <div>
                                    <dt className="font-medium text-gray-700">Email:</dt>
                                    <dd className="break-all">{profesor.email || '-'}</dd>
                                </div>
                            </dl>
                        </div>
                    )}

                    {/* School Information */}
                    <div>
                        <h3 className="ecotic-dialog-section-title">
                            Informații Școală
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="text-small font-medium text-gray-700">
                                    Nume Instituție: <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={editedData.numeInstitutieInvatamant || ''}
                                    onChange={(e) => handleFieldChange('numeInstitutieInvatamant', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors.numeInstitutieInvatamant && <p className="mt-1 text-xs text-error">{errors.numeInstitutieInvatamant}</p>}
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">CUI:</label>
                                <input
                                    type="text"
                                    value={editedData.cui || ''}
                                    onChange={(e) => handleFieldChange('cui', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['cui'] && <p className="mt-1 text-xs text-error">{errors['cui']}</p>}
                            </div>
                            <div>
                                {/* Doar afisare (owner, 29.09): codul SE il aloca serverul la aprobare.
                                    Regula: scoala gasita dupa SIIIR isi pastreaza codul; fara potrivire, cod nou.
                                    Daca adminul schimba SIIIR-ul aici, codul randului nu mai e valabil. */}
                                <span className="text-small font-medium text-gray-700">Cod SE:</span>
                                <p className="mt-1 flex min-h-11 items-center px-3 py-2 text-base text-dark">
                                    {editedData.codSiiirUnitate !== formData?.codSiiirUnitate ? (
                                        <span className="text-gray-600">se stabilește după salvare, după codul SIIIR</span>
                                    ) : (
                                        editedData.codSe || codSeRand || <CodSeLaAprobare />
                                    )}
                                </p>
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">Telefon:</label>
                                <input
                                    type="text"
                                    value={editedData.telefon || ''}
                                    onChange={(e) => handleFieldChange('telefon', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['telefon'] && <p className="mt-1 text-xs text-error">{errors['telefon']}</p>}
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">Număr Elevi:</label>
                                <input
                                    type="number"
                                    value={editedData.numarElevi || ''}
                                    onChange={(e) => handleFieldChange('numarElevi', parseInt(e.target.value) || null)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['numarElevi'] && <p className="mt-1 text-xs text-error">{errors['numarElevi']}</p>}
                            </div>
                            <div className="col-span-2">
                                <label className="text-small font-medium text-gray-700">Reprezentant Legal:</label>
                                <input
                                    type="text"
                                    value={editedData.numeReprezentatLegal || ''}
                                    onChange={(e) => handleFieldChange('numeReprezentatLegal', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['numeReprezentatLegal'] && <p className="mt-1 text-xs text-error">{errors['numeReprezentatLegal']}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Address */}
                    <div>
                        <h3 className="ecotic-dialog-section-title">
                            Adresă
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-small font-medium text-gray-700">Județ:</label>
                                <select
                                    value={editedData.adresa?.judet || ''}
                                    onChange={(e) => handleFieldChange('adresa.judet', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                >
                                    <option value="">Selectați județ</option>
                                    {counties.map(county => (
                                        <option key={county} value={county}>{county}</option>
                                    ))}
                                </select>
                                {errors['adresa.judet'] && <p className="mt-1 text-xs text-error">{errors['adresa.judet']}</p>}
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">Oraș:</label>
                                <input
                                    type="text"
                                    value={editedData.adresa?.oras || ''}
                                    onChange={(e) => handleFieldChange('adresa.oras', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['adresa.oras'] && <p className="mt-1 text-xs text-error">{errors['adresa.oras']}</p>}
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">Comună:</label>
                                <input
                                    type="text"
                                    value={editedData.adresa?.comuna || ''}
                                    onChange={(e) => handleFieldChange('adresa.comuna', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['adresa.comuna'] && <p className="mt-1 text-xs text-error">{errors['adresa.comuna']}</p>}
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">Sat:</label>
                                <input
                                    type="text"
                                    value={editedData.adresa?.sat || ''}
                                    onChange={(e) => handleFieldChange('adresa.sat', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['adresa.sat'] && <p className="mt-1 text-xs text-error">{errors['adresa.sat']}</p>}
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">Stradă:</label>
                                <input
                                    type="text"
                                    value={editedData.adresa?.strada || ''}
                                    onChange={(e) => handleFieldChange('adresa.strada', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['adresa.strada'] && <p className="mt-1 text-xs text-error">{errors['adresa.strada']}</p>}
                            </div>
                            <div>
                                <label className="text-small font-medium text-gray-700">Număr:</label>
                                <input
                                    type="text"
                                    value={editedData.adresa?.numar || ''}
                                    onChange={(e) => handleFieldChange('adresa.numar', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['adresa.numar'] && <p className="mt-1 text-xs text-error">{errors['adresa.numar']}</p>}
                            </div>
                            {/* Bloc / scara / etaj / apartament nu se mai cer si nu se mai editeaza (decizie
                                owner, 18.09); raman in date, doar nu se randeaza. */}
                            <div>
                                <label className="text-small font-medium text-gray-700">Cod Poștal:</label>
                                <input
                                    type="text"
                                    value={editedData.adresa?.codPostal || ''}
                                    onChange={(e) => handleFieldChange('adresa.codPostal', e.target.value)}
                                    className="mt-1 min-h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta"
                                    disabled={loading}
                                />
                                {errors['adresa.codPostal'] && <p className="mt-1 text-xs text-error">{errors['adresa.codPostal']}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="ecotic-dialog-footer">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="flex h-11 items-center rounded-md border border-input bg-white px-4 text-small font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:h-9"
                        >
                            Anulare
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="flex h-11 items-center rounded-md bg-magenta px-4 text-small font-medium text-white hover:bg-magenta-hover disabled:cursor-not-allowed disabled:opacity-50 md:h-9"
                        >
                            Salvează Corectările
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


